import { useMemo, useRef } from 'react';
import { ValidationLevel, ValidationProps } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Configuration } from 'app/components/metro-line/types';
import { SubscriberGroupsTranslation } from '../../constants';
import { SubscriberGroupItemValidation } from '../types-store';
import { SubscriberGroupSectionType } from './types';
import { SubscriberGroupActionType } from 'app/area/subscriber-groups/form/types';

const isValid = (validationProps: ValidationProps[], level: ValidationLevel) =>
  !validationProps.some(prop => (prop?.messages?.length ?? 0) > 0 && prop?.level === level);

export const getSubscriberGroupsRoute = (
  actionType: SubscriberGroupActionType,
  id: number,
  section?: SubscriberGroupSectionType
) => actionType && {
  [SubscriberGroupActionType.add]: routeConstants.subscriberGroupsAddFor(section),
  [SubscriberGroupActionType.edit]: routeConstants.subscriberGroupsEditFor(id, section)
}[actionType];

export const useSubscriberGroupsConfiguration = (
  actionType: SubscriberGroupActionType,
  id: number,
  validationErrors: SubscriberGroupItemValidation
): Configuration<SubscriberGroupSectionType>[] => {

  const detailsSectionRef = useRef<HTMLDivElement>();
  const translationsSectionRef = useRef<HTMLDivElement>();
  const externalMembersSectionRef = useRef<HTMLDivElement>();
  const userMembersSectionRef = useRef<HTMLDivElement>();

  return useMemo(() => {

    const getSectionRoute = (section: SubscriberGroupSectionType) => getSubscriberGroupsRoute(actionType, id, section);
    const renderLanguageOnTop = !isDefinedIncludingZero(id);
    const languageSection = {
      section: SubscriberGroupSectionType.Translations,
      visible: true,
      url: getSectionRoute(SubscriberGroupSectionType.Translations),
      iconUrl: '/i/case-sections/actions.svg',
      label: (translation: SubscriberGroupsTranslation) => translation.descriptions_and_translations,
      ref: translationsSectionRef,
      isValid: isValid([validationErrors.language, validationErrors.longDescription], ValidationLevel.error)
    };

    return [
      ...(renderLanguageOnTop ? [languageSection] : []),
      {
        section: SubscriberGroupSectionType.Details,
        visible: true,
        iconUrl: '/i/case-sections/default.svg',
        url: getSectionRoute(SubscriberGroupSectionType.Details),
        label: (translation: SubscriberGroupsTranslation) => translation.details,
        ref: detailsSectionRef,
        isValid: isValid([validationErrors.selfService, validationErrors.disabled], ValidationLevel.error)
      },
      {
        section: SubscriberGroupSectionType.UserMembers,
        visible: true,
        iconUrl: '/i/case-sections/connected-cases.svg',
        url: getSectionRoute(SubscriberGroupSectionType.UserMembers),
        label: (translation: SubscriberGroupsTranslation) => translation.user_members_title,
        ref: userMembersSectionRef,
        isValid: true
      },
      {
        section: SubscriberGroupSectionType.ExternalMembers,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(SubscriberGroupSectionType.ExternalMembers),
        label: (translation: SubscriberGroupsTranslation) => translation. external_members_title,
        ref: externalMembersSectionRef,

        isValid: !validationErrors?.emailAddress?.messages?.length
          && !validationErrors?.name?.messages?.length
      },
      ...(!renderLanguageOnTop ? [languageSection] : [])
    ];
  }, [id, actionType, detailsSectionRef, translationsSectionRef, validationErrors]);
};
